import * as Yup from "yup";
import {
  BANNER_APPEARANCE_COLORS,
  BANNER_IMAGE_MAX_SIZE_KB,
  BANNER_IMAGE_MIN_HEIGHT,
  BANNER_IMAGE_MIN_WIDTH,
  BANNER_INTERNAL_ROUTE_OPTIONS,
  BANNER_LANDING_BODY_MAX_LENGTH,
  BANNER_LANDING_BUTTON_TEXT_MAX_LENGTH,
  BANNER_LANDING_CALLOUT_MAX_LENGTH,
  BANNER_LANDING_TITLE_MAX_LENGTH,
  BANNER_SHORT_TEXT_MAX_LENGTH,
  BannerAppearanceColor,
  BannerCtaType,
  Banner,
  MAX_ACTIVE_BANNERS,
} from "../../models/banners";
import { bannerLandingBodyLength, isBannerRichTextEmpty } from "./bannerRichText";
import { getApiErrorCode, getApiValidationIssues, getApiValidationIssueFields, sanitizeApiErrorMessage } from "../../utils/functions/api-errors";

export const getActiveBannerCount = (banners: Banner[], excludeId?: string): number =>
  banners.filter((banner) => banner.sectionKey === "banners_carousel" && banner.status === "active" && banner.id !== excludeId).length;

/**
 * Las mismas reglas que valida la API (BN-0XX). Duplicarlas acá es para que el usuario vea el
 * error sin ir y volver; la autoridad sigue siendo el backend, y `mapApiErrorToFormField` traduce
 * lo que rechace al campo del formulario.
 */

const ALLOWED_INTERNAL_ROUTES = BANNER_INTERNAL_ROUTE_OPTIONS.map((option) => option.value);

const isHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export const requiresUrlCtaValue = (ctaType: BannerCtaType): boolean =>
  ctaType === "external_url" || ctaType === "embedded_pdf";

/**
 * Lo que el usuario cargo en la seccion de Pagina Interna, tal como esta en el formulario.
 *
 * Es el BORRADOR, no lo persistido: el modal de "vas a perder la configuracion" tiene que saltar
 * tambien cuando alguien escribio la landing y cambia de CTA sin haber guardado nunca. Mirar
 * `banner.landingPage` solo cubria el caso de edicion.
 */
export interface BannerLandingDraft {
  heroImageUrl: string;
  title: string;
  body: string;
  callout: string;
  color: BannerAppearanceColor;
  buttonEnabled: boolean;
  buttonText: string;
  buttonValue: string;
}

/**
 * El color y el switch tambien cuentan: son dos clicks que el usuario ya hizo y que cambiar de
 * CTA descarta igual que el texto. Sin ellos, elegir color y prender el boton y despues cambiar
 * de CTA borraba esa configuracion sin preguntar.
 */
export const bannerLandingDraftHasData = (draft: BannerLandingDraft): boolean =>
  draft.heroImageUrl.trim() !== "" ||
  draft.title.trim() !== "" ||
  !isBannerRichTextEmpty(draft.body) ||
  draft.callout.trim() !== "" ||
  draft.color !== BANNER_APPEARANCE_COLORS[0] ||
  draft.buttonEnabled ||
  draft.buttonText.trim() !== "" ||
  draft.buttonValue.trim() !== "";

export const bannerCtaHasLoadedData = (
  ctaType: BannerCtaType,
  ctaValue: string,
  landing: BannerLandingDraft
): boolean =>
  ctaType === "internal_page" ? bannerLandingDraftHasData(landing) : ctaValue.trim() !== "";

const INTERNAL_PAGE = (ctaType: unknown): boolean => ctaType === "internal_page";

/**
 * Criterio de aceptacion: "Toggle Off no permite cargar texto/destino". Con el switch apagado los
 * campos no se renderizan, el payload manda `null` y estas reglas ni se evaluan.
 */
const landingButtonOn = (ctaType: unknown, enabled: unknown): boolean =>
  ctaType === "internal_page" && enabled === true;

export const bannerFormValidationSchema = Yup.object({
  name: Yup.string().trim().required("El nombre es obligatorio"),
  shortText: Yup.string()
    .max(
      BANNER_SHORT_TEXT_MAX_LENGTH,
      `Máximo ${BANNER_SHORT_TEXT_MAX_LENGTH} caracteres`
    )
    .nullable(),
  imageUrl: Yup.string().required("La imagen es obligatoria"),
  imageSizeKb: Yup.number()
    .max(BANNER_IMAGE_MAX_SIZE_KB, `La imagen no puede superar ${BANNER_IMAGE_MAX_SIZE_KB} KB`)
    .required(),
  imageWidth: Yup.number()
    .min(
      BANNER_IMAGE_MIN_WIDTH,
      `La imagen debe medir al menos ${BANNER_IMAGE_MIN_WIDTH}x${BANNER_IMAGE_MIN_HEIGHT}px`
    )
    .required(),
  imageHeight: Yup.number()
    .min(
      BANNER_IMAGE_MIN_HEIGHT,
      `La imagen debe medir al menos ${BANNER_IMAGE_MIN_WIDTH}x${BANNER_IMAGE_MIN_HEIGHT}px`
    )
    .required(),
  ctaType: Yup.string().required("El tipo de acción es obligatorio"),
  startDate: Yup.date().nullable().when("status", {
    is: "scheduled",
    then: (schema) => schema.typeError("Ingresá una fecha válida").required("La fecha de inicio es obligatoria"),
  }),
  endDate: Yup.date().nullable()
    .when("status", {
      is: "scheduled",
      then: (schema) => schema.typeError("Ingresá una fecha válida").required("La fecha de fin es obligatoria"),
    })
    // Fuera del `when`: un banner activo tambien puede tener vencimiento, y ahi el orden importa igual.
    .test("after-start", "La fecha de fin debe ser posterior al inicio", function (value) {
      return !value || !this.parent.startDate || value.getTime() > new Date(this.parent.startDate).getTime();
    }),
  ctaValue: Yup.string()
    .nullable()
    .test("cta-value", "Valor de acción inválido", function (value) {
      const ctaType = this.parent.ctaType as BannerCtaType;

      if (ctaType === "internal_page") {
        return value
          ? this.createError({ message: "Con landing page no se completa un destino" })
          : true;
      }

      if (ctaType === "internal_route") {
        return ALLOWED_INTERNAL_ROUTES.includes(String(value ?? ""))
          ? true
          : this.createError({ message: "Elegí una de las secciones permitidas" });
      }

      if (!value || !isHttpUrl(value)) {
        return this.createError({ message: "Tiene que ser una URL que empiece con http o https" });
      }
      return true;
    }),

  /**
   * BN-010 — Landing de "Pagina Interna". Todo lo de abajo solo aplica con
   * `ctaType === "internal_page"`: con cualquier otro CTA los campos no se renderizan y no tienen
   * que bloquear el submit.
   *
   * El ticket marca "opcional" UNICAMENTE al callout, asi que hero, titulo, cuerpo y color son
   * obligatorios. Los topes son los mismos que valida BN-020 del lado de la API.
   */
  landingHeroImageUrl: Yup.string().when("ctaType", {
    is: INTERNAL_PAGE,
    then: (schema) => schema.required("La imagen principal es obligatoria"),
  }),

  landingTitle: Yup.string().when("ctaType", {
    is: INTERNAL_PAGE,
    then: (schema) =>
      schema
        .trim()
        .required("El título de la página es obligatorio")
        .max(BANNER_LANDING_TITLE_MAX_LENGTH, `Máximo ${BANNER_LANDING_TITLE_MAX_LENGTH} caracteres`),
  }),

  landingBody: Yup.string().when("ctaType", {
    is: INTERNAL_PAGE,
    then: (schema) =>
      schema
        .test("landing-body-required", "El cuerpo de la página es obligatorio", (value) =>
          !isBannerRichTextEmpty(value ?? "")
        )
        // Se mide el TEXTO VISIBLE, igual que el contador del editor: ver `bannerRichText`.
        .test(
          "landing-body-max",
          `Máximo ${BANNER_LANDING_BODY_MAX_LENGTH} caracteres`,
          (value) => bannerLandingBodyLength(value ?? "") <= BANNER_LANDING_BODY_MAX_LENGTH
        ),
  }),

  landingCallout: Yup.string().when("ctaType", {
    is: INTERNAL_PAGE,
    then: (schema) =>
      schema.max(
        BANNER_LANDING_CALLOUT_MAX_LENGTH,
        `Máximo ${BANNER_LANDING_CALLOUT_MAX_LENGTH} caracteres`
      ),
  }),

  landingColor: Yup.string().when("ctaType", {
    is: INTERNAL_PAGE,
    then: (schema) =>
      schema
        .required("Elegí un color")
        .oneOf(BANNER_APPEARANCE_COLORS, "Elegí uno de los colores disponibles"),
  }),

  landingButtonText: Yup.string().when(["ctaType", "landingButtonEnabled"], {
    is: landingButtonOn,
    then: (schema) =>
      schema
        .trim()
        .required("El texto del botón es obligatorio")
        .max(
          BANNER_LANDING_BUTTON_TEXT_MAX_LENGTH,
          `Máximo ${BANNER_LANDING_BUTTON_TEXT_MAX_LENGTH} caracteres`
        ),
  }),

  landingButtonType: Yup.string().when(["ctaType", "landingButtonEnabled"], {
    is: landingButtonOn,
    then: (schema) => schema.required("Elegí el tipo de destino"),
  }),

  landingButtonValue: Yup.string().when(["ctaType", "landingButtonEnabled"], {
    is: landingButtonOn,
    then: (schema) =>
      schema.test("landing-button-value", "Destino inválido", function (value) {
        const buttonType = this.parent.landingButtonType as string | null;
        if (buttonType === "internal_route") {
          return ALLOWED_INTERNAL_ROUTES.includes(String(value ?? ""))
            ? true
            : this.createError({ message: "Elegí una de las secciones permitidas" });
        }
        if (!value || !isHttpUrl(value)) {
          return this.createError({
            message: "Tiene que ser una URL que empiece con http o https",
          });
        }
        return true;
      }),
  }),
});

const API_FIELD_TO_FORM_FIELD: Record<string, string> = {
  shortText: "shortText",
  ctaValue: "ctaValue",
  imageSizeKb: "imageUrl",
  imageWidth: "imageUrl",
  imageHeight: "imageUrl",
  status: "status",
  name: "name",
  imageUrl: "imageUrl",
  ctaType: "ctaType",
  sortOrder: "sortOrder",
  startDate: "startDate",
  endDate: "endDate",
  landingPage: "ctaType",
};

/**
 * El backend manda el campo de la landing anidado (`landingPage.title`). Se resuelve por sufijo
 * en vez de enumerar las nueve rutas, y asi un campo nuevo del contrato cae en su control sin
 * tocar dos tablas.
 */
const LANDING_FIELD_TO_FORM_FIELD: Record<string, string> = {
  heroImageUrl: "landingHeroImageUrl",
  title: "landingTitle",
  body: "landingBody",
  importantMessage: "landingCallout",
  appearanceColor: "landingColor",
  actionButtonEnabled: "landingButtonEnabled",
  actionButtonText: "landingButtonText",
  actionButtonType: "landingButtonType",
  actionButtonValue: "landingButtonValue",
};

const LANDING_FIELD_PREFIX = "landingPage.";

const resolveFormField = (field: string): string | undefined =>
  field.startsWith(LANDING_FIELD_PREFIX)
    ? LANDING_FIELD_TO_FORM_FIELD[field.slice(LANDING_FIELD_PREFIX.length)]
    : API_FIELD_TO_FORM_FIELD[field];

/**
 * El backend responde `error.details[].field`, así que el error cae en el control correcto sin
 * parsear texto libre.
 */
export const mapApiErrorToFormFields = (error: unknown): Record<string, string> => {
  const code = getApiErrorCode(error);
  const issues = getApiValidationIssues(error) ?? [];
  const messages: Record<string, string> = {};

  for (const issue of issues) {
    const field = getApiValidationIssueFields({ details: [issue] })[0];
    const formField = resolveFormField(field);
    if (formField) {
      const message = typeof issue.message === "string" ? issue.message.trim() : "";
      const issueCode = typeof issue.code === "string" ? issue.code : code;
      messages[formField] = message
        ? sanitizeApiErrorMessage(message)
        : API_ERROR_MESSAGES[issueCode ?? ""] ?? "Revisá este campo";
    }
  }

  return messages;
};

export const BANNER_ACTIVE_LIMIT_TITLE = "Límite de banners alcanzado";
export const BANNER_ACTIVE_LIMIT_BODY =
  "Ya tenés 4 banners activos, el máximo permitido. Desactivá uno para activar este.";
export const BANNER_ACTIVE_LIMIT_PRIMARY = "Ver banners activos";
export const BANNER_ACTIVE_FILTER_HREF = "/config/home-banners?status=active";
export const BANNER_SORT_ORDER_TEMP_OFFSET = 10_000;

export const movedBannerRows = <T,>(rows: T[], fromIndex: number, toIndex: number): T[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || toIndex >= rows.length) {
    return rows;
  }
  const next = [...rows];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

export const sortBannersByOrder = (banners: Banner[]): Banner[] =>
  [...banners].sort((left, right) => left.sortOrder - right.sortOrder || left.id.localeCompare(right.id));

export const getBannerDisplayOrder = (banner: Pick<Banner, "sortOrder">): number =>
  banner.sortOrder + 1;

/**
 * Reasigna `sortOrder` a la lista que se acaba de arrastrar, repartiendo entre sus filas LAS
 * POSICIONES QUE ESAS MISMAS FILAS YA OCUPABAN.
 *
 * Antes esto numeraba 0..n-1 sobre las filas visibles. Sin filtro da lo mismo (la lista visible es
 * toda la lista), pero con `?status=active` — a donde manda el modal de "límite alcanzado" — las
 * filas visibles son solo los activos: renumerarlas 0..n les pisaba el `sortOrder` a los inactivos,
 * que no se patchean y conservan el suyo, y el orden global quedaba mezclado.
 *
 * Repartiendo las posiciones ocupadas, los banners que no se ven no se tocan y los que se
 * arrastraron quedan en el orden nuevo. De paso solo se patchea lo que realmente se movió.
 */
export const withReassignedBannerSortOrder = (visible: Banner[]): Banner[] => {
  const slots = visible.map((banner) => banner.sortOrder).sort((left, right) => left - right);
  return visible.map((banner, index) => ({ ...banner, sortOrder: slots[index] ?? index }));
};

export type BannerSortOrderPatch = { id: string; sortOrder: number };

export const bannerSortOrderSaveSteps = (
  previous: Banner[],
  next: Banner[]
): BannerSortOrderPatch[][] => {
  const previousOrder = new Map(previous.map((banner) => [banner.id, banner.sortOrder]));
  const patches = next
    .filter((banner) => previousOrder.get(banner.id) !== banner.sortOrder)
    .map((banner) => ({ id: banner.id, sortOrder: banner.sortOrder }));
  if (patches.length === 0) return [];

  const activeIds = new Set(next.filter((banner) => banner.status === "active").map((banner) => banner.id));
  const activePatches = patches.filter((patch) => activeIds.has(patch.id));
  if (activePatches.length === 0) return [patches];

  const occupied = Math.max(
    0,
    ...previous.map((banner) => banner.sortOrder),
    ...next.map((banner) => banner.sortOrder)
  );
  return [
    activePatches.map((patch, index) => ({
      id: patch.id,
      sortOrder: occupied + BANNER_SORT_ORDER_TEMP_OFFSET + index,
    })),
    patches,
  ];
};

export const API_ERROR_MESSAGES: Record<string, string> = {
  BANNER_SHORT_TEXT_TOO_LONG: `Máximo ${BANNER_SHORT_TEXT_MAX_LENGTH} caracteres`,
  BANNER_IMAGE_TOO_LARGE: `La imagen no puede superar ${BANNER_IMAGE_MAX_SIZE_KB} KB`,
  BANNER_IMAGE_TOO_SMALL: `La imagen debe medir al menos ${BANNER_IMAGE_MIN_WIDTH}x${BANNER_IMAGE_MIN_HEIGHT}px`,
  BANNER_CTA_VALUE_INVALID_URL: "Tiene que ser una URL que empiece con http o https",
  BANNER_CTA_VALUE_INVALID_ROUTE: "Elegí una de las secciones permitidas",
  BANNER_CTA_VALUE_NOT_ALLOWED: "Con landing page no se completa un destino",
  BANNER_LANDING_PAGE_REQUIRED: "Falta configurar la landing page",
  BANNER_SORT_ORDER_TAKEN: "Otro banner activo ya usa ese orden",
  BANNER_SCHEDULE_INVALID: "La fecha de fin tiene que ser posterior a la de inicio",
  BANNER_ACTION_BUTTON_INCOMPLETE: "El botón de acción necesita texto, tipo y destino",
  BANNER_LANDING_HERO_IMAGE_REQUIRED: "La imagen principal es obligatoria",
  BANNER_LANDING_TITLE_TOO_LONG: `Máximo ${BANNER_LANDING_TITLE_MAX_LENGTH} caracteres`,
  BANNER_LANDING_BODY_TOO_LONG: `Máximo ${BANNER_LANDING_BODY_MAX_LENGTH} caracteres`,
  BANNER_LANDING_CALLOUT_TOO_LONG: `Máximo ${BANNER_LANDING_CALLOUT_MAX_LENGTH} caracteres`,
  BANNER_LANDING_BUTTON_TEXT_TOO_LONG: `Máximo ${BANNER_LANDING_BUTTON_TEXT_MAX_LENGTH} caracteres`,
  BANNER_LANDING_APPEARANCE_COLOR_INVALID: "Elegí uno de los colores disponibles",
};



const SCHEDULE_DATE_FORMAT = new Intl.DateTimeFormat("es-AR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const formatScheduleDate = (value: string | null): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : SCHEDULE_DATE_FORMAT.format(parsed);
};

/**
 * Texto de la ventana de vigencia para el listado. Las fechas no se veian en ningun lado, asi que
 * no habia forma de saber cuando vence un banner sin abrirlo.
 */
export const formatBannerSchedule = (banner: {
  startDate: string | null;
  endDate: string | null;
}): string => {
  const start = formatScheduleDate(banner.startDate);
  const end = formatScheduleDate(banner.endDate);

  if (start && end) return `${start} — ${end}`;
  if (start) return `Desde ${start}`;
  if (end) return `Hasta ${end}`;
  return "Sin vencimiento";
};


export const SCHEDULE_AT_CAPACITY_WARNING =
  "Ya hay 4 banners activos. Si al llegar la fecha el cupo sigue lleno, este banner queda inactivo y hay que activarlo a mano.";

/**
 * Cuenta solo los banners ACTIVOS: los que estan en Programado esperando al cron no ocupan cupo,
 * porque recien compiten por un lugar cuando les llega la fecha.
 *
 * `currentBannerId` se excluye para que editar un banner activo y pasarlo a Programado no se
 * cuente a si mismo como ocupando el lugar que esta por liberar.
 */
export const countActiveBannersExcluding = (
  banners: { id: string; status: string }[],
  currentBannerId?: string
): number =>
  banners.filter((banner) => banner.status === "active" && banner.id !== currentBannerId).length;

/** La advertencia es informativa: programar con el cupo lleno se permite igual. */
export const showsScheduleAtCapacityWarning = (
  status: string,
  activeBannerCount: number,
  maxActive: number = MAX_ACTIVE_BANNERS
): boolean => status === "scheduled" && activeBannerCount >= maxActive;
